/**
 * ============================================================================
 * Outsource 模块 API 工厂函数
 * ============================================================================
 *
 * @description 委外管理模块的所有 API 请求方法
 * @source 对应后端文件:
 * - backend/app/Modules/Outsource/Routes/api.php
 * - backend/app/Modules/Outsource/Controllers/OutsourceOrderController.php
 * - backend/app/Modules/Outsource/Controllers/OutsourceShipmentController.php
 * - backend/app/Modules/Outsource/Controllers/OutsourceReceivingController.php
 * - backend/app/Modules/Outsource/Controllers/OutsourceReturnController.php
 * @last-updated 2026-03-13
 * ============================================================================
 */

import type {
  OutsourceOrder,
  OutsourceOrderItem,
  OutsourceShipment,
  OutsourceReceiving,
  OutsourceReturn,
  OutsourceDashboardData,
  CreateOutsourceOrderForm,
  UpdateOutsourceOrderForm,
  CreateOutsourceShipmentForm,
  CreateOutsourceReceivingForm,
  HandleOutsourceReceivingExceptionForm,
  UpdateOutsourceReceivingQualityForm,
  CreateOutsourceReturnForm,
  OutsourceOrderQueryParams,
  OutsourceShipmentQueryParams,
  OutsourceReceivingQueryParams,
  OutsourceReturnQueryParams,
} from '../../types/outsource';

import type { PaginationResult } from '../../types/api';
import type { IRequestClient } from '../core/client';
import { API_ROUTES } from '../routes';

/**
 * 创建委外管理 API
 *
 * @param client - 请求客户端（Web端或App端适配器）
 * @returns 委外管理 API 方法集合
 */
export function createOutsourceApi(client: IRequestClient) {
  const routes = API_ROUTES.outsource;

  return {
    // ==================== 委外订单管理 ====================

    /** 获取委外订单列表 */
    getOrderList: (params?: OutsourceOrderQueryParams) =>
      client.get<PaginationResult<OutsourceOrder>>(routes.orders, { params }),

    /** 获取委外订单详情 */
    getOrderDetail: (id: number | string) =>
      client.get<OutsourceOrder>(routes.outsourceOrders(id)),

    /** 创建委外订单 */
    createOrder: (data: CreateOutsourceOrderForm) =>
      client.post<OutsourceOrder>(routes.orders, data),

    /** 更新委外订单 */
    updateOrder: (id: number | string, data: UpdateOutsourceOrderForm) =>
      client.put<OutsourceOrder>(routes.outsourceOrders(id), data),

    /** 删除委外订单 */
    deleteOrder: (id: number | string) =>
      client.delete<void>(routes.outsourceOrders(id)),

    /** 提交审核 */
    submitOrder: (id: number | string) =>
      client.post<OutsourceOrder>(routes.ordersSubmit(id)),

    /** 审核通过 */
    approveOrder: (id: number | string, data?: { promise_date?: string }) =>
      client.post<OutsourceOrder>(routes.ordersApprove(id), data),

    /** 驳回订单 */
    rejectOrder: (id: number | string, data: { reason: string }) =>
      client.post<OutsourceOrder>(routes.ordersReject(id), data),

    /** 取消订单 */
    cancelOrder: (id: number | string, data: { reason: string }) =>
      client.post<OutsourceOrder>(routes.ordersCancel(id), data),

    /** 关闭订单 */
    closeOrder: (id: number | string) =>
      client.post<OutsourceOrder>(routes.ordersClose(id)),

    /** 结算订单 */
    settleOrder: (id: number | string) =>
      client.post<OutsourceOrder>(routes.ordersSettle(id)),

    /** 获取订单明细 */
    getOrderItems: (id: number | string) =>
      client.get<OutsourceOrderItem[]>(routes.ordersItems(id)),

    /** 获取订单发料记录 */
    getOrderShipments: (id: number | string) =>
      client.get<OutsourceShipment[]>(routes.ordersShipments(id)),

    /** 获取订单收货记录 */
    getOrderReceivings: (id: number | string) =>
      client.get<OutsourceReceiving[]>(routes.ordersReceivings(id)),

    // ==================== 委外发料管理 ====================

    /** 获取发料记录列表 */
    getShipmentList: (params?: OutsourceShipmentQueryParams) =>
      client.get<PaginationResult<OutsourceShipment>>(routes.shipments, { params }),

    /** 获取发料详情 */
    getShipmentDetail: (id: number | string) =>
      client.get<OutsourceShipment>(routes.outsourceShipments(id)),

    /** 创建发料单 */
    createShipment: (data: CreateOutsourceShipmentForm) =>
      client.post<OutsourceShipment>(routes.shipments, data),

    /** 扫码发料（APP端） */
    scanShipment: (data: CreateOutsourceShipmentForm) =>
      client.post<OutsourceShipment>(routes.shipmentsScan, data),

    // ==================== 委外收货管理 ====================

    /** 获取收货记录列表 */
    getReceivingList: (params?: OutsourceReceivingQueryParams) =>
      client.get<PaginationResult<OutsourceReceiving>>(routes.receivings, { params }),

    /** 获取收货详情 */
    getReceivingDetail: (id: number | string) =>
      client.get<OutsourceReceiving>(routes.outsourceReceivings(id)),

    /** 创建收货单 */
    createReceiving: (data: CreateOutsourceReceivingForm) =>
      client.post<OutsourceReceiving>(routes.receivings, data),

    /** 扫码收货（APP端） */
    scanReceiving: (data: CreateOutsourceReceivingForm) =>
      client.post<OutsourceReceiving>(routes.receivingsScan, data),

    /** 更新收货质检结果 */
    qualityReceiving: (id: number | string, data: UpdateOutsourceReceivingQualityForm) =>
      client.post<OutsourceReceiving>(routes.receivingsQuality(id), data),

    /** 处理收货异常 */
    handleReceivingException: (id: number | string, data: HandleOutsourceReceivingExceptionForm) =>
      client.post<OutsourceReceiving>(routes.receivingsException(id), data),

    /** 收货入库 */
    warehouseReceiving: (id: number | string) =>
      client.post<OutsourceReceiving>(routes.receivingsWarehouse(id)),

    // ==================== 委外退料管理 ====================

    /** 获取退料记录列表 */
    getReturnList: (params?: OutsourceReturnQueryParams) =>
      client.get<PaginationResult<OutsourceReturn>>(routes.returns, { params }),

    /** 获取退料详情 */
    getReturnDetail: (id: number | string) =>
      client.get<OutsourceReturn>(routes.outsourceReturns(id)),

    /** 创建退料单 */
    createReturn: (data: CreateOutsourceReturnForm) =>
      client.post<OutsourceReturn>(routes.returns, data),

    /** 退料入库 */
    warehouseReturn: (id: number | string) =>
      client.post<OutsourceReturn>(routes.returnsWarehouse(id)),

    // ==================== 委外看板 ====================

    /** 获取看板数据 */
    getDashboardData: () =>
      client.get<OutsourceDashboardData>(routes.dashboard),
  };
}
